/**
 * Configuration options for retry with exponential backoff
 */
interface RetryConfig {
  /** Maximum number of attempts (default: 3) */
  maxAttempts?: number
  /** Base delay in milliseconds (default: 1000) */
  baseDelay?: number
  /** Maximum delay in milliseconds (default: 10000) */
  maxDelay?: number
  /** Function name for logging (optional) */
  fnName?: string
}

/**
 * Result of a retry operation
 */
interface RetryResult<T> {
  success: boolean
  result?: T
  lastError?: unknown
  attemptsMade: number
}

/**
 * Calculate exponential backoff delay with max cap
 */
function calculateDelay(
  attempt: number,
  baseDelay: number,
  maxDelay: number
): number {
  return Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay)
}

/**
 * Sleep for the specified number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Format error for logging
 */
function formatError(error: unknown): { error: string; errorType: string } {
  return {
    error: error instanceof Error ? error.message : String(error),
    errorType: error instanceof Error ? error.constructor.name : typeof error,
  }
}

/**
 * Core retry loop implementation shared by both retry functions
 */
async function executeRetryLoop<T>(
  operation: (
    attempt: number,
    maxAttempts: number
  ) => Promise<{ success: boolean; result?: T; shouldContinue: boolean }>,
  config: RetryConfig,
  logPrefix: string
): Promise<RetryResult<T>> {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    fnName = "function",
  } = config

  let lastError: unknown

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    console.log(`[${logPrefix}] ${fnName} - Attempt ${attempt}/${maxAttempts}`)

    try {
      const { success, result, shouldContinue } = await operation(
        attempt,
        maxAttempts
      )

      if (success) {
        console.log(`[${logPrefix}] ${fnName} - Success on attempt ${attempt}`)
        return {
          success: true,
          result,
          attemptsMade: attempt,
        }
      }

      if (!shouldContinue) {
        console.log(
          `[${logPrefix}] ${fnName} - Condition not met on attempt ${attempt}`
        )
      }
    } catch (error) {
      lastError = error
      console.error(
        `[${logPrefix}] ${fnName} - Attempt ${attempt}/${maxAttempts} failed:`,
        formatError(error)
      )
    }

    // If this was the last attempt, don't wait
    if (attempt === maxAttempts) {
      break
    }

    // Calculate exponential backoff delay and wait
    const delay = calculateDelay(attempt, baseDelay, maxDelay)
    console.log(`[${logPrefix}] ${fnName} - Waiting ${delay}ms before retry...`)
    await sleep(delay)
  }

  console.error(`[${logPrefix}] ${fnName} - All ${maxAttempts} attempts failed`)
  return {
    success: false,
    lastError,
    attemptsMade: maxAttempts,
  }
}

/**
 * Retry a function with exponential backoff
 * @param fn - Async function to retry
 * @param config - Retry configuration
 * @returns Promise that resolves to RetryResult
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  config: RetryConfig = {}
): Promise<RetryResult<T>> {
  return executeRetryLoop(
    async () => {
      const result = await fn()
      return {
        success: true,
        result,
        shouldContinue: false, // Not used for retry pattern
      }
    },
    config,
    "retry"
  )
}

/**
 * Wait for a condition to be true with exponential backoff
 * @param condition - Function that returns true when condition is met
 * @param config - Retry configuration
 * @returns Promise that resolves to true if condition was met, false if timeout
 */
async function waitForCondition(
  condition: () => boolean | Promise<boolean>,
  config: RetryConfig = {}
): Promise<boolean> {
  const result = await executeRetryLoop(
    async () => {
      const isReady = await condition()
      return {
        success: isReady,
        result: isReady,
        shouldContinue: !isReady,
      }
    },
    { maxAttempts: 5, ...config },
    "waitFor"
  )

  return result.success
}
